/**
 * SecureEmbed Login Page
 *
 * Simple username/password authentication via AWS Cognito SDK
 * Supports pre-populated username via ?username=<value> URL parameter
 * Supports TOTP MFA challenge during login
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Eye, EyeOff, AlertCircle, Loader2 } from 'lucide-react';

// Get config from window (injected by config.js)
declare global {
  interface Window {
    SECUREEMBED_CONFIG?: {
      COGNITO_USER_POOL_ID: string;
      COGNITO_CLIENT_ID: string;
      API_ENDPOINT: string;
      WS_ENDPOINT: string;
      REGION: string;
      RUM_APP_MONITOR_ID?: string;
      RUM_IDENTITY_POOL_ID?: string;
      RUM_GUEST_ROLE_ARN?: string;
    };
  }
}

const Login: React.FC = () => {
  const navigate = useNavigate();

  // H9: AbortController for fetch cleanup
  const abortControllerRef = useRef<AbortController | null>(null);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [showNewPasswordForm, setShowNewPasswordForm] = useState(false);
  const [challengeSession, setChallengeSession] = useState<string | null>(null);
  const [showMfaForm, setShowMfaForm] = useState(false);
  const [mfaCode, setMfaCode] = useState('');

  const getRoleFromToken = (idToken?: string): string | undefined => {
    if (!idToken) return undefined;
    try {
      const parts = idToken.split('.');
      if (parts.length !== 3) return undefined;
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      const groups = payload['cognito:groups'];
      const groupList = Array.isArray(groups) ? groups : (typeof groups === 'string' ? groups.split(',') : []);
      if (groupList.includes('Admins')) return 'admin';
      if (groupList.includes('Monitors')) return 'monitor';
    } catch {
      return undefined;
    }
    return undefined;
  };

  // M12: Removed username URL parameter to prevent enumeration
  // Username pre-population via URL is a security risk

  // H9: Cleanup AbortController on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Check if already logged in
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      window.location.href = '/';
    }
  }, [navigate]);

  // Complete login by storing tokens and redirecting
  const completeLogin = (authResult: any) => {
    localStorage.setItem('authToken', authResult.AccessToken);
    localStorage.setItem('idToken', authResult.IdToken);
    localStorage.setItem('refreshToken', authResult.RefreshToken || '');
    const role = getRoleFromToken(authResult.IdToken);
    localStorage.setItem('user', JSON.stringify({ username, role }));
    window.location.href = '/';
  };

  // Handle login with Cognito InitiateAuth
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    const config = window.SECUREEMBED_CONFIG;
    if (!config) {
      setError('Configuration not loaded. Please refresh the page.');
      setIsLoading(false);
      return;
    }

    try {
      // H9: Cancel any in-flight request before starting new one
      if (abortControllerRef.current) abortControllerRef.current.abort();
      abortControllerRef.current = new AbortController();

      // Use Cognito InitiateAuth API via fetch
      const response = await fetch(
        `https://cognito-idp.${config.REGION}.amazonaws.com/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-amz-json-1.1',
            'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth',
          },
          body: JSON.stringify({
            AuthFlow: 'USER_PASSWORD_AUTH',
            ClientId: config.COGNITO_CLIENT_ID,
            AuthParameters: {
              USERNAME: username,
              PASSWORD: password,
            },
          }),
          signal: abortControllerRef.current.signal,
        }
      );

      const data = await response.json();

      if (!response.ok) {
        // Handle specific Cognito errors
        const errorType = data.__type || '';
        if (errorType.includes('NotAuthorizedException')) {
          throw new Error('Incorrect username or password');
        } else if (errorType.includes('UserNotFoundException')) {
          throw new Error('User does not exist');
        } else if (errorType.includes('UserNotConfirmedException')) {
          throw new Error('User is not confirmed. Please contact admin.');
        } else {
          throw new Error(data.message || 'Authentication failed');
        }
      }

      // Check for challenges
      if (data.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
        setChallengeSession(data.Session);
        setShowNewPasswordForm(true);
        setIsLoading(false);
        return;
      }

      if (data.ChallengeName === 'SOFTWARE_TOKEN_MFA') {
        setChallengeSession(data.Session);
        setShowMfaForm(true);
        setIsLoading(false);
        return;
      }

      // Success - store tokens
      if (data.AuthenticationResult) {
        completeLogin(data.AuthenticationResult);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle MFA code verification
  const handleMfaVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    const config = window.SECUREEMBED_CONFIG;
    if (!config || !challengeSession) {
      setError('Session expired. Please try again.');
      setIsLoading(false);
      return;
    }

    try {
      // H9: Cancel any in-flight request before starting new one
      if (abortControllerRef.current) abortControllerRef.current.abort();
      abortControllerRef.current = new AbortController();

      const response = await fetch(
        `https://cognito-idp.${config.REGION}.amazonaws.com/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-amz-json-1.1',
            'X-Amz-Target': 'AWSCognitoIdentityProviderService.RespondToAuthChallenge',
          },
          body: JSON.stringify({
            ChallengeName: 'SOFTWARE_TOKEN_MFA',
            ClientId: config.COGNITO_CLIENT_ID,
            Session: challengeSession,
            ChallengeResponses: {
              USERNAME: username,
              SOFTWARE_TOKEN_MFA_CODE: mfaCode,
            },
          }),
          signal: abortControllerRef.current.signal,
        }
      );

      const data = await response.json();

      if (!response.ok) {
        const errorType = data.__type || '';
        if (errorType.includes('CodeMismatchException')) {
          throw new Error('Invalid authentication code. Please try again.');
        } else if (errorType.includes('ExpiredCodeException')) {
          throw new Error('Code has expired. Please generate a new code.');
        } else {
          throw new Error(data.message || 'MFA verification failed');
        }
      }

      if (data.AuthenticationResult) {
        completeLogin(data.AuthenticationResult);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'MFA verification failed');
      setMfaCode('');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle new password challenge
  const handleNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    const config = window.SECUREEMBED_CONFIG;
    if (!config || !challengeSession) {
      setError('Session expired. Please try again.');
      setIsLoading(false);
      return;
    }

    try {
      // H9: Cancel any in-flight request before starting new one
      if (abortControllerRef.current) abortControllerRef.current.abort();
      abortControllerRef.current = new AbortController();

      const response = await fetch(
        `https://cognito-idp.${config.REGION}.amazonaws.com/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-amz-json-1.1',
            'X-Amz-Target': 'AWSCognitoIdentityProviderService.RespondToAuthChallenge',
          },
          body: JSON.stringify({
            ChallengeName: 'NEW_PASSWORD_REQUIRED',
            ClientId: config.COGNITO_CLIENT_ID,
            Session: challengeSession,
            ChallengeResponses: {
              USERNAME: username,
              NEW_PASSWORD: newPassword,
            },
          }),
          signal: abortControllerRef.current.signal,
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to set new password');
      }

      // Success - store tokens
      if (data.AuthenticationResult) {
        completeLogin(data.AuthenticationResult);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set new password');
    } finally {
      setIsLoading(false);
    }
  };

  // Determine the form title
  const getFormTitle = () => {
    if (showMfaForm) return 'Authentication Code';
    if (showNewPasswordForm) return 'Set New Password';
    return 'Sign In';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0369A1] via-[#1F2937] to-[#0369A1] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/10 backdrop-blur mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">SecureEmbed</h1>
          <p className="text-white/70">Platform Management Portal</p>
        </div>

        {/* Login Card */}
        <div className="bg-gray-100 rounded-2xl shadow-2xl p-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-6 text-center">
            {getFormTitle()}
          </h2>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* MFA Code Form */}
          {showMfaForm ? (
            <form onSubmit={handleMfaVerify} className="space-y-4">
              <p className="text-sm text-gray-600 mb-4">
                Enter the 6-digit code from your authenticator app.
              </p>

              <div>
                <label htmlFor="mfaCode" className="block text-sm font-medium text-gray-700 mb-1">
                  Authentication Code
                </label>
                <input
                  id="mfaCode"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={mfaCode}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setMfaCode(val);
                  }}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0369A1] focus:border-transparent text-center text-2xl tracking-widest font-mono"
                  placeholder="000000"
                  required
                  maxLength={6}
                  autoFocus
                />
              </div>

              <button
                type="submit"
                disabled={isLoading || mfaCode.length !== 6}
                className="w-full py-3 px-4 bg-[#0369A1] text-white rounded-lg font-medium hover:bg-[#374151] transition-colors disabled:opacity-50 flex items-center justify-center"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Verifying...
                  </>
                ) : (
                  'Verify'
                )}
              </button>

              <button
                type="button"
                onClick={() => {
                  setShowMfaForm(false);
                  setChallengeSession(null);
                  setMfaCode('');
                  setError(null);
                }}
                className="w-full py-2 text-sm text-gray-500 hover:text-gray-700"
              >
                Back to Sign In
              </button>
            </form>
          ) : showNewPasswordForm ? (
            /* New Password Form */
            <form onSubmit={handleNewPassword} className="space-y-4">
              <p className="text-sm text-gray-600 mb-4">
                Your password must be changed before continuing.
              </p>

              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <div className="relative">
                  <input
                    id="newPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0369A1] focus:border-transparent"
                    placeholder="Enter new password"
                    required
                    minLength={8}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Must be at least 8 characters
                </p>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 px-4 bg-[#0369A1] text-white rounded-lg font-medium hover:bg-[#374151] transition-colors disabled:opacity-50 flex items-center justify-center"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Setting Password...
                  </>
                ) : (
                  'Set Password & Continue'
                )}
              </button>
            </form>
          ) : (
            /* Login Form */
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                  Username
                </label>
                <input
                  id="username"
                  type="text"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0369A1] focus:border-transparent"
                  placeholder="Enter your username"
                  required
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0369A1] focus:border-transparent pr-12"
                    placeholder="Enter your password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 px-4 bg-[#0369A1] text-white rounded-lg font-medium hover:bg-[#374151] transition-colors disabled:opacity-50 flex items-center justify-center"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-white/50 text-sm">
            SecureEmbed  &bull; Powered by AWS
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
